import { useQuery } from '@tanstack/react-query'
import { sessionQuery } from '@/lib/api/queries'
import { useDemo } from '@/lib/demo/demo-context'
import { FRAME_SAMPLE } from '@/lib/demo/frame'
import { consoleRoleFor, useConsoleRole } from '@/lib/demo/use-console-role'
import { currentAccount } from '@/lib/session'

/** Two letters for an avatar: "Alpha Plumbing" → "AP", "kim@alpha.test" → "KA". */
export function initials(source: string): string {
  return source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

export interface WorkspaceRow {
  id: string
  name: string
  initials: string
  sub: string
  current: boolean
  warm: boolean
}

export interface FrameIdentity {
  workspace: { name: string; initials: string; sub: string } | null
  workspaces: WorkspaceRow[]
  /** `email` is who is signed in; the dev sample has none. */
  person: { name: string; initials: string; roleLabel: string; email: string | null } | null
  /** Calls needing attention: the nav count and the bell. Null when we have no source for it. */
  attention: number | null
}

/**
 * Who and where, for the sidebar and header: the session's real account and
 * person, or the prototype's sample with the dev controls on. We have no plan
 * data, so the workspace's second line is the person's role there -- never an
 * invented plan name.
 */
export function useFrameIdentity(): FrameIdentity {
  const demo = useDemo()
  const role = useConsoleRole()
  const session = useQuery(sessionQuery)

  if (import.meta.env.DEV && demo.enabled) {
    const sample = FRAME_SAMPLE
    const user = sample.user(demo.role)
    return {
      workspace: { name: sample.workspace.name, initials: sample.workspace.initials, sub: sample.workspace.plan },
      workspaces: sample.workspaceRows.map((row) => ({ id: row.initials, ...row })),
      person: { ...user, roleLabel: role.label, email: null },
      attention: demo.dataState === 'empty' ? null : sample.attention,
    }
  }

  const data = session.data
  if (!data) return { workspace: null, workspaces: [], person: null, attention: null }
  const current = currentAccount(data)
  const name = data.full_name?.trim() || data.email
  return {
    workspace: current
      ? { name: current.name, initials: initials(current.name), sub: consoleRoleFor(current.role).label }
      : null,
    workspaces: (data.accounts ?? []).map((account) => ({
      id: account.id,
      name: account.name,
      initials: initials(account.name),
      sub: consoleRoleFor(account.role).label,
      current: account.id === current?.id,
      warm: false,
    })),
    person: { name, initials: initials(name), roleLabel: role.label, email: data.email },
    attention: null,
  }
}
